import { jwtDecode } from 'jwt-decode';
import { refreshAbcToken } from './api';

interface TokenPayload {
  exp: number;
  iat: number;
  sub: string;
  pid: string;
  aud: string;
  iss: string;
}

interface RefreshResponse {
  accessToken: string;
  refreshToken: string;
  expireIn: number;
}

// JWT 토큰 디코딩
export function decodeToken(token: string): TokenPayload | null {
  try {
    return jwtDecode<TokenPayload>(token);
  } catch (error) {
    console.error('Token decode error:', error);
    return null;
  }
}

// 토큰 만료 확인
export function isTokenExpired(token: string): boolean {
  const decoded = decodeToken(token);
  if (!decoded) return true;
  
  // 현재 시간과 만료 시간 비교 (30초 여유 시간)
  const currentTime = Math.floor(Date.now() / 1000);
  const expirationTime = decoded.exp;
  
  return currentTime >= (expirationTime - 30);
}

// 토큰 만료까지 남은 시간 (초)
export function getTokenExpirationTime(token: string): number {
  const decoded = decodeToken(token);
  if (!decoded) return 0;
  
  const currentTime = Math.floor(Date.now() / 1000);
  const expirationTime = decoded.exp;
  
  return Math.max(0, expirationTime - currentTime);
}

// refresh 토큰을 사용하여 새로운 액세스 토큰 발급
export async function refreshAccessToken(): Promise<string | null> {
  try {
    const abcAuthInfo: string | null = localStorage.getItem('abc-auth-info');
    if (!abcAuthInfo) {
      console.error('No refresh token available');
      return null;
    }

    const abcAuthInfoJson: any = JSON.parse(abcAuthInfo);
    const refreshToken = abcAuthInfoJson.refreshToken;

    if (!refreshToken) {
      console.error('No refresh token found in auth info');
      return null;
    }

    const res = await refreshAbcToken(refreshToken);
    
    if (res.status === 200 && res.data) {
      const tokenData = res.data.data;
      const newAccessToken = tokenData.accessToken;
      
      if (newAccessToken) {
        // 토큰 정보 업데이트
        localStorage.setItem('abc-auth-info', JSON.stringify(tokenData));
        localStorage.setItem('abc-auth-token', newAccessToken);
        
        return newAccessToken;
      }
    }
    
    console.error('Invalid refresh response');
    return null;
    
  } catch (error) {
    console.error('Token refresh error:', error);
    
    // refresh 실패 시 저장된 토큰 정보 삭제
    localStorage.removeItem('abc-auth-token');
    localStorage.removeItem('abc-auth-info');
    
    return null;
  }
}

// 토큰이 만료되었는지 확인하고 필요시 refresh
export async function ensureValidToken(): Promise<string | null> {
  const currentToken = localStorage.getItem('abc-auth-token');
  
  if (!currentToken) {
    return null;
  }

  if (isTokenExpired(currentToken)) {
    const newToken = await refreshAccessToken();

    if (!newToken) {
      console.error('Token refresh failed');
      // 토큰 갱신 실패 시 로그인 페이지로 이동
      if (typeof window !== 'undefined') {
        window.location.href = '/login';
      }
      return null;
    }

    return newToken;
  }

  return currentToken;
}

// 토큰 상태 모니터링 (선택사항)
export function startTokenMonitoring() {
  if (typeof window === 'undefined') return; // 서버사이드에서는 실행하지 않음

  const checkTokenExpiration = async () => {
    const token = localStorage.getItem('abc-auth-token');
    if (token) {
      const timeUntilExpiry = getTokenExpirationTime(token);
      
      if (timeUntilExpiry <= 300) { // 5분 이내 만료
        await refreshAccessToken();
      }
    }
  };

  // 1분마다 토큰 상태 확인
  const intervalId = setInterval(checkTokenExpiration, 60000);
  
  // 초기 확인
  checkTokenExpiration();

  // cleanup 함수 반환 (필요시 중지할 수 있도록)
  return () => {
    clearInterval(intervalId);
  };
}
